/**
 * Main Sidebar Component
 * Features: Logo, Navigation, Services, Dark Mode Toggle, User Profile
 */

import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  ListItemButton,
  Avatar,
  Divider,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  ListSubheader,
} from '@mui/material';
import { Settings, LogOut, LogIn, SquarePen, MessageCircleWarning } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../types';
import { ChatSessionsList } from '../chat/ChatSessionsList';
import { ShareConversationDialog } from '../chat/ShareConversationDialog';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useSidebarConversations } from './useSidebarConversations';
import { SidebarBrand } from './SidebarBrand';
import { SidebarQuickSettings } from './SidebarQuickSettings';
import { ProfileMenuItemRow } from './ProfileMenuItemRow';
import { profileMenuGroupLabelClassName, withServiceGroups } from './profileMenuConfig';
import { usePackageFeatures } from '../../features/package-features/hooks/usePackageFeatures';
import { useServiceMenu } from '../../features/services/hooks/useServiceMenu';
import { SidebarProjectsEntry } from '../../features/projects/components/SidebarProjectsEntry';
import { ProjectPickerDialog } from '../../features/projects/components/ProjectPickerDialog';
import { useMoveConversation } from '../../features/projects/hooks/useMoveConversation';
import { useProjects } from '../../features/projects/hooks/useProjects';

// ============================================
// Props Interface
// ============================================

interface SidebarProps {
  user: User;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileDrawer?: boolean; // If true, render as drawer content (no fixed positioning)
  onNavigate?: () => void; // Callback when navigation item is clicked (for closing drawer)
  onOpenSettings?: () => void;
  onSignIn?: () => void; // Anonymous users: open the sign-in modal
}

// ============================================
// Sidebar Component
// ============================================

export const Sidebar: React.FC<SidebarProps> = ({
  user,
  isCollapsed,
  onToggleCollapse,
  isMobileDrawer = false,
  onNavigate,
  onOpenSettings,
  onSignIn,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout, isAnonymous } = useAuth();
  const { sessions, isLoading, renameConversation, archiveConversation, deleteConversation } =
    useSidebarConversations();
  const { groups: serviceGroups, refresh: refreshServices } = useServiceMenu(
    isAnonymous ? null : user.publicId
  );
  const { isLocked: isFeatureLocked, refresh: refreshFeatures } = usePackageFeatures(
    isAnonymous ? null : user.publicId
  );
  const menuGroups = useMemo(
    () => withServiceGroups(serviceGroups, isFeatureLocked),
    [isFeatureLocked, serviceGroups]
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [shareSessionId, setShareSessionId] = useState<string | null>(null);
  const [projectPickerSessionId, setProjectPickerSessionId] = useState<string | null>(null);
  const { enabled: projectsEnabled } = useProjects();
  const moveConversation = useMoveConversation();
  const isMenuOpen = Boolean(anchorEl);
  const displayName = user.fullName || user.userName;
  const avatarInitial = (displayName || user.email || 'U').charAt(0).toUpperCase();
  const activeSessionId = location.pathname.startsWith('/chat/')
    ? location.pathname.split('/chat/')[1]?.split('/')[0]
    : undefined;

  const isActive = (path: string) => location.pathname === path;

  const handleNavigation = () => {
    if (onNavigate) {
      onNavigate();
    }
  };

  const handleSessionSelect = (sessionId: string) => {
    navigate(`/chat/${sessionId}`);
    handleNavigation();
  };

  const handleNewChat = () => {
    navigate('/new-chat');
    handleNavigation();
  };

  const handleArchive = async (sessionId: string) => {
    const wasActive = activeSessionId === sessionId;
    const success = await archiveConversation(sessionId);

    if (success && wasActive) {
      navigate('/new-chat');
      handleNavigation();
    }
  };

  const handleDelete = async (sessionId: string) => {
    const wasActive = activeSessionId === sessionId;
    const success = await deleteConversation(sessionId);

    if (success && wasActive) {
      navigate('/new-chat');
      handleNavigation();
    }
  };

  const handleShare = (sessionId: string) => {
    setShareSessionId(sessionId);
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    void refreshFeatures();
    void refreshServices();
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuNavigation = () => {
    handleMenuClose();
    handleNavigation();
  };

  const handleUserSettings = () => {
    handleMenuClose();
    handleNavigation();
    onOpenSettings?.();
  };

  const handleFeedback = () => {
    handleMenuClose();
    handleNavigation();
    navigate('/feedback');
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    setIsLogoutDialogOpen(true);
  };

  const handleLogoutDialogClose = () => {
    if (!isLoggingOut) {
      setIsLogoutDialogOpen(false);
    }
  };

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      // Call logout function from AuthContext
      // This will call POST /auth/logout and clear local state.
      await logout();
      setIsLogoutDialogOpen(false);
      navigate('/', { replace: true });
    } catch (error) {
      // Error is already handled in AuthContext logout function
      console.error('Logout handler error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <Box
      className={`flex h-screen flex-col border-r border-gray-200 bg-white transition-all duration-300 dark:border-slate-700 dark:bg-slate-800 ${
        isMobileDrawer ? 'w-64' : isCollapsed ? 'w-16' : 'w-64'
      } ${!isMobileDrawer ? 'fixed left-0 top-0' : ''}`}
    >
      <SidebarBrand
        isCollapsed={isCollapsed}
        isMobileDrawer={isMobileDrawer}
        isAnonymous={isAnonymous}
        onToggleCollapse={onToggleCollapse}
        onNavigate={handleNavigation}
      />

      {!isAnonymous && (
        <Box className={`px-3 pt-3 ${isCollapsed && !isMobileDrawer ? 'flex justify-center' : ''}`}>
          <Tooltip
            title={isCollapsed && !isMobileDrawer ? t('sidebar.newChat') : ''}
            placement="right"
            arrow
          >
            <ListItemButton
              component={Link}
              to="/new-chat"
              onClick={handleNavigation}
              className={`rounded-lg border border-blue-500/40 bg-blue-500/10 text-blue-600 transition-all hover:border-blue-500/70 hover:bg-blue-500/20 dark:border-blue-400/30 dark:text-blue-400 dark:hover:border-blue-400/60 dark:hover:bg-blue-500/20 ${
                isCollapsed && !isMobileDrawer ? 'justify-center' : 'gap-2'
              }`}
              sx={{
                py: 1,
                px: isCollapsed && !isMobileDrawer ? 1 : 2,
                minHeight: 40,
              }}
            >
              <SquarePen size={17} className="shrink-0" />
              {(!isCollapsed || isMobileDrawer) && (
                <Typography variant="body2" className="font-semibold">
                  {t('sidebar.newChat')}
                </Typography>
              )}
            </ListItemButton>
          </Tooltip>
        </Box>
      )}

      {isAnonymous && onSignIn && (
        <Box className={`px-3 pt-3 ${isCollapsed && !isMobileDrawer ? 'flex justify-center' : ''}`}>
          <Tooltip
            title={isCollapsed && !isMobileDrawer ? t('common.signIn') : ''}
            placement="right"
            arrow
          >
            <ListItemButton
              onClick={onSignIn}
              className={`rounded-lg bg-teal-600 text-white transition-colors hover:bg-teal-500 ${
                isCollapsed && !isMobileDrawer ? 'justify-center' : 'gap-2'
              }`}
              sx={{
                py: 1,
                px: isCollapsed && !isMobileDrawer ? 1 : 2,
                minHeight: 40,
                '&:hover': { backgroundColor: 'rgb(20 184 166)' },
              }}
            >
              <LogIn size={17} className="shrink-0" />
              {(!isCollapsed || isMobileDrawer) && (
                <Typography variant="body2" className="font-semibold">
                  {t('common.signIn')}
                </Typography>
              )}
            </ListItemButton>
          </Tooltip>
        </Box>
      )}

      {!isAnonymous && (
        <SidebarProjectsEntry
          isCollapsed={isCollapsed}
          isMobileDrawer={isMobileDrawer}
          onNavigate={handleNavigation}
        />
      )}

      {/* Conversations */}
      <Box className="flex-1 overflow-y-auto py-4">
        {!isAnonymous && (!isCollapsed || isMobileDrawer) && (
          <>
            <Divider className="mx-3 mb-4" />
            <Typography
              variant="caption"
              className="px-7 py-2 font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400"
            >
              {t('sidebar.conversations')}
            </Typography>
            <Box className="mt-2">
              <ChatSessionsList
                sessions={sessions}
                activeSessionId={activeSessionId}
                onSessionSelect={handleSessionSelect}
                onNewChat={handleNewChat}
                onShare={handleShare}
                onRename={renameConversation}
                onArchive={handleArchive}
                onDelete={handleDelete}
                onSelectProject={projectsEnabled ? setProjectPickerSessionId : undefined}
                onRemoveFromProject={(sessionId) => void moveConversation(sessionId, null)}
                embedded
                isLoading={isLoading}
                groupBySource
              />
            </Box>
          </>
        )}
      </Box>

      {/* Bottom Section: Notifications + Dark Mode Toggle + User Profile */}
      <Box className="border-t border-gray-200 dark:border-slate-700">
        <SidebarQuickSettings
          isCollapsed={isCollapsed}
          isMobileDrawer={isMobileDrawer}
          isAnonymous={isAnonymous}
        />

        {!isAnonymous && (
          <Box
            className={`cursor-pointer bg-gray-50 py-4 transition-colors hover:bg-gray-100 dark:bg-slate-900/50 dark:hover:bg-slate-900/70 ${isCollapsed && !isMobileDrawer ? 'px-2' : 'px-4'}`}
            onClick={handleProfileClick}
            sx={{ minHeight: 44 }} // Touch target optimization
          >
            {isCollapsed && !isMobileDrawer ? (
              <Tooltip
                title={`${displayName} (${user.subscription?.packageName || 'free'})`}
                placement="right"
                arrow
              >
                <Box className="flex justify-center">
                  <Avatar
                    src={user.avatar}
                    alt={displayName}
                    className="bg-gradient-to-br from-indigo-500 to-pink-500"
                    sx={{ width: 32, height: 32 }}
                  >
                    {avatarInitial}
                  </Avatar>
                </Box>
              </Tooltip>
            ) : (
              <>
                <Box className="flex items-center gap-3">
                  <Avatar
                    src={user.avatar}
                    alt={displayName}
                    className="bg-gradient-to-br from-indigo-500 to-pink-500"
                    sx={{ width: 40, height: 40 }}
                  >
                    {avatarInitial}
                  </Avatar>
                  <Box className="min-w-0 flex-1">
                    <Box className="flex min-w-0 items-center gap-2">
                      <Typography
                        variant="body2"
                        className="min-w-0 truncate font-semibold text-gray-900 dark:text-slate-100"
                      >
                        {displayName}
                      </Typography>
                      {user.subscription && (
                        <Typography
                          variant="caption"
                          className={`inline-block shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 font-semibold ${
                            user.subscription.packageCode === 'ENTERPRISE'
                              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                              : user.subscription.packageCode === 'PRO'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : user.subscription.isTrialing
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300'
                          }`}
                        >
                          {user.subscription.packageName}
                          {user.subscription.isTrialing && ` (${t('common.trial')})`}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      className="block truncate text-gray-500 dark:text-slate-400"
                    >
                      {user.email}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* User Profile Menu */}
        {!isAnonymous && (
          <Menu
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: isCollapsed ? 'left' : 'right',
            }}
            slotProps={{
              paper: {
                className: 'mt-2 min-w-[200px] rounded-lg shadow-lg dark:bg-slate-800',
              },
            }}
          >
            {menuGroups.flatMap(({ id: groupId, labelKey: groupLabelKey, items }, index) => [
              ...(index > 0 ? [<Divider key={`${groupId}-divider`} className="my-1" />] : []),
              <ListSubheader
                key={`${groupId}-label`}
                disableSticky
                className={profileMenuGroupLabelClassName}
                sx={{ bgcolor: 'transparent', lineHeight: '1.25rem' }}
              >
                {t(groupLabelKey)}
              </ListSubheader>,
              ...items.map((item) => (
                <ProfileMenuItemRow
                  key={item.id}
                  item={item}
                  selected={isActive(item.path)}
                  onNavigate={handleMenuNavigation}
                />
              )),
              ...(groupId === 'account'
                ? [
                    <MenuItem
                      key={`${groupId}-settings`}
                      onClick={handleUserSettings}
                      className="gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700"
                    >
                      <Settings size={18} className="text-gray-500 dark:text-slate-400" />
                      <Typography variant="body2">{t('common.settings')}</Typography>
                    </MenuItem>,
                  ]
                : []),
            ])}
            <Divider className="my-1" />
            <ListSubheader
              disableSticky
              className={profileMenuGroupLabelClassName}
              sx={{ bgcolor: 'transparent', lineHeight: '1.25rem' }}
            >
              {t('sidebar.menuGroups.support')}
            </ListSubheader>
            <MenuItem
              onClick={handleFeedback}
              className="gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              <MessageCircleWarning size={18} className="text-gray-500 dark:text-slate-400" />
              <Typography variant="body2">{t('sidebar.feedback')}</Typography>
            </MenuItem>
            <Divider className="my-1" />
            <MenuItem
              onClick={handleLogoutClick}
              className="gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <LogOut size={18} />
              <Typography variant="body2">{t('common.logout')}</Typography>
            </MenuItem>
          </Menu>
        )}
      </Box>
      <ConfirmDialog
        open={isLogoutDialogOpen}
        title={t('logoutConfirm.title')}
        message={t('logoutConfirm.message')}
        confirmText={t('common.logout')}
        cancelText={t('logoutConfirm.cancel')}
        confirmColor="error"
        loading={isLoggingOut}
        onClose={handleLogoutDialogClose}
        onConfirm={() => void handleConfirmLogout()}
      />
      <ProjectPickerDialog
        open={Boolean(projectPickerSessionId)}
        currentProjectId={sessions.find((s) => s.id === projectPickerSessionId)?.projectPublicId}
        onSelect={async (project) => {
          if (projectPickerSessionId) await moveConversation(projectPickerSessionId, project);
        }}
        onClose={() => setProjectPickerSessionId(null)}
      />
      <ShareConversationDialog
        open={Boolean(shareSessionId)}
        sessionId={shareSessionId}
        onClose={() => setShareSessionId(null)}
      />
    </Box>
  );
};
